/**
 * ConfidenceRatingOverlay — Anki-style post-answer confidence self-assessment.
 *
 * Slides up from the bottom after a learner answers an MCQ or quiz question.
 * Shows 4 spaced-repetition buttons (Again / Hard / Good / Easy) aligned with
 * the SM-2 scale so the adaptive scheduler can update the learner's review
 * interval accordingly.
 *
 * New interface (task 2 / TeachIQ adaptive):
 *   learnerId            — string
 *   skillId              — string
 *   confidenceRatingEnabled — boolean (only renders when true)
 *   autoDismissMs        — number (default 8000)
 *   onRated              — (rating: 1|2|3|4) => void
 *   onDismissed          — () => void
 *
 * Legacy interface (video-marker confidence_rating overlay type) is handled
 * by the presence of `question` / `markerId` / `sceneId` / `domain` props.
 * In legacy mode the component shows a 5-level scale and emits a learner event
 * instead of calling the adaptive API.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { adaptiveAPI } from '../../services/api';
import { emitLearnerEvent } from '../../services/learnerEvents';

// ─── Anki-style 4-button config ───────────────────────────────────────────────
const ANKI_RATINGS = [
  { value: 1, label: 'Again', color: '#ef4444', bg: 'rgba(239,68,68,0.12)',  key: '1' },
  { value: 2, label: 'Hard',  color: '#f97316', bg: 'rgba(249,115,22,0.12)', key: '2' },
  { value: 3, label: 'Good',  color: '#22c55e', bg: 'rgba(34,197,94,0.12)',  key: '3' },
  { value: 4, label: 'Easy',  color: '#3b82f6', bg: 'rgba(59,130,246,0.12)', key: '4' },
];

// ─── Legacy 5-level config ─────────────────────────────────────────────────────
const LEGACY_RATINGS = [
  { value: 1, label: 'Not at all', color: '#ef4444' },
  { value: 2, label: 'Slightly',   color: '#f97316' },
  { value: 3, label: 'Moderately', color: '#eab308' },
  { value: 4, label: 'Very',       color: '#84cc16' },
  { value: 5, label: 'Completely', color: '#22c55e' },
];

const DEFAULT_AUTO_DISMISS_MS = 8000;

// ─── Component ────────────────────────────────────────────────────────────────

export default function ConfidenceRatingOverlay({
  // New interface
  learnerId,
  skillId,
  confidenceRatingEnabled,
  autoDismissMs = DEFAULT_AUTO_DISMISS_MS,
  onRated,
  onDismissed,
  // Legacy interface
  question,
  markerId,
  sceneId,
  domain,
  onDismiss,
}) {
  const isLegacy = Boolean(question || markerId);

  // New interface: don't render if disabled
  if (!isLegacy && !confidenceRatingEnabled) return null;

  return isLegacy
    ? <LegacyConfidenceRating
        question={question}
        markerId={markerId}
        sceneId={sceneId}
        domain={domain}
        onDismiss={onDismiss}
      />
    : <AnkiConfidenceRating
        learnerId={learnerId}
        skillId={skillId}
        autoDismissMs={autoDismissMs}
        onRated={onRated}
        onDismissed={onDismissed}
      />;
}

// ─── Anki-style post-MCQ rating ───────────────────────────────────────────────

function AnkiConfidenceRating({ learnerId, skillId, autoDismissMs, onRated, onDismissed }) {
  const [visible, setVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [countdown, setCountdown] = useState(Math.round(autoDismissMs / 1000));
  const mountTimeRef = useRef(Date.now());
  const timerRef = useRef(null);
  const countdownRef = useRef(null);

  // Slide up after a short delay so the learner sees the quiz result first
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 120);
    return () => clearTimeout(t);
  }, []);

  // Auto-dismiss countdown
  useEffect(() => {
    timerRef.current = setTimeout(() => {
      handleDismiss();
    }, autoDismissMs);

    countdownRef.current = setInterval(() => {
      setCountdown((c) => Math.max(0, c - 1));
    }, 1000);

    return () => {
      clearTimeout(timerRef.current);
      clearInterval(countdownRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDismiss = useCallback(() => {
    clearTimeout(timerRef.current);
    clearInterval(countdownRef.current);
    setVisible(false);
    setTimeout(() => { if (onDismissed) onDismissed(); }, 220);
  }, [onDismissed]);

  const handleRate = useCallback(async (rating) => {
    if (submitting) return;
    setSubmitting(true);
    clearTimeout(timerRef.current);
    clearInterval(countdownRef.current);

    const timeTakenSeconds = Math.round((Date.now() - mountTimeRef.current) / 1000);

    try {
      await adaptiveAPI.rateReview(learnerId, skillId, {
        confidence: rating,
        time_taken_seconds: timeTakenSeconds,
      });
    } catch {
      // fire-and-forget — adaptive rating must never block the UI
    }

    if (onRated) onRated(rating);
    setVisible(false);
    setTimeout(() => { if (onDismissed) onDismissed(); }, 220);
  }, [submitting, learnerId, skillId, onRated, onDismissed]);

  // Keyboard: 1-4 keys map to ratings
  useEffect(() => {
    const handler = (e) => {
      const n = parseInt(e.key, 10);
      if (n >= 1 && n <= 4) handleRate(n);
      if (e.key === 'Escape') handleDismiss();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleRate, handleDismiss]);

  return (
    <div
      style={{
        ...ankiOverlayStyle,
        transform: visible ? 'translateY(0)' : 'translateY(100%)',
        opacity: visible ? 1 : 0,
      }}
      role="dialog"
      aria-modal="true"
      aria-label="How confident were you?"
    >
      <div style={ankiCardStyle}>
        {/* Header */}
        <div style={ankiHeaderStyle}>
          <span style={ankiHeadingStyle}>How confident were you?</span>
          <button
            onClick={handleDismiss}
            aria-label="Dismiss confidence rating"
            style={closeBtnStyle}
          >
            ✕
          </button>
        </div>

        {/* Subtitle + countdown */}
        <p style={ankiSubStyle}>
          Rate your recall to help us schedule the next review.
          {countdown > 0 && (
            <span style={{ color: '#64748b', marginLeft: 6 }}>Auto-dismiss in {countdown}s</span>
          )}
        </p>

        {/* Rating buttons */}
        <div
          style={ankiRowStyle}
          role="group"
          aria-label="Confidence rating buttons"
        >
          {ANKI_RATINGS.map(({ value, label, color, bg, key }) => (
            <button
              key={value}
              onClick={() => handleRate(value)}
              disabled={submitting}
              aria-label={`Rate confidence: ${label} (${key})`}
              title={`${label} — press ${key}`}
              style={{
                ...ankiBtnBase,
                borderColor: color,
                color,
                background: bg,
              }}
            >
              <span style={{ fontSize: 15, fontWeight: 700 }}>{label}</span>
              <span style={{ fontSize: 10, opacity: 0.6, marginTop: 2 }}>{key}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Legacy in-video confidence check ─────────────────────────────────────────

const FORCE_CONTINUE_AFTER_MS = 3000;

function LegacyConfidenceRating({ question, markerId, sceneId, domain, onDismiss }) {
  const [selected, setSelected] = useState(null);
  const [canSkip, setCanSkip] = useState(false);
  const mountTimeRef = useRef(Date.now());

  useEffect(() => {
    const t = setTimeout(() => setCanSkip(true), FORCE_CONTINUE_AFTER_MS);
    return () => clearTimeout(t);
  }, []);

  const handleRate = async (value) => {
    if (selected !== null) return;
    setSelected(value);

    await emitLearnerEvent({
      event_type: 'overlay_answered',
      overlay_type: 'confidence_rating',
      marker_id: markerId,
      confidence_rating: value,
      scene_id: sceneId,
      domain,
      time_to_answer_ms: Date.now() - mountTimeRef.current,
    });

    if (onDismiss) onDismiss();
  };

  const handleForceContinue = async () => {
    await emitLearnerEvent({
      event_type: 'overlay_skipped',
      overlay_type: 'confidence_rating',
      marker_id: markerId,
      scene_id: sceneId,
      domain,
      time_to_answer_ms: Date.now() - mountTimeRef.current,
    });
    if (onDismiss) onDismiss();
  };

  return (
    <div style={legacyOverlayStyle}>
      <div style={legacyCardStyle}>
        <div style={legacyHeaderStyle}>
          <span style={legacyLabelStyle}>Confidence Check</span>
        </div>

        <p style={legacyPromptStyle}>{question || 'How confident are you about this concept?'}</p>

        <div style={legacyRatingRowStyle}>
          {LEGACY_RATINGS.map(({ value, label, color }) => (
            <button
              key={value}
              onClick={() => handleRate(value)}
              disabled={selected !== null}
              title={`${value} — ${label}`}
              aria-label={`Rate confidence ${value} out of 5: ${label}`}
              style={{
                ...legacyRatingBtnStyle,
                borderColor: selected === value ? color : '#334155',
                background: selected === value ? `${color}22` : 'transparent',
                color: selected === value ? color : '#94a3b8',
              }}
            >
              <span style={{ fontSize: 22, fontWeight: 700, color: selected === value ? color : '#64748b' }}>
                {value}
              </span>
              <span style={{ fontSize: 10, lineHeight: 1.2, marginTop: 4 }}>{label}</span>
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 20 }}>
          <span style={{ fontSize: 12, color: '#64748b' }}>
            {canSkip ? '' : 'Rate your confidence to continue…'}
          </span>
          {canSkip && selected === null && (
            <button onClick={handleForceContinue} style={legacyContinueBtnStyle}>
              Continue →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Anki-style styles ─────────────────────────────────────────────────────────

const ankiOverlayStyle = {
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 200,
  transition: 'transform 0.22s cubic-bezier(0.34,1.56,0.64,1), opacity 0.2s ease',
  padding: '0 16px 16px',
  display: 'flex',
  justifyContent: 'center',
};

const ankiCardStyle = {
  background: '#1e293b',
  border: '1px solid #334155',
  borderRadius: 14,
  padding: '20px 24px',
  maxWidth: 560,
  width: '100%',
  boxShadow: '0 -4px 32px rgba(0,0,0,0.45)',
};

const ankiHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 6,
};

const ankiHeadingStyle = {
  fontSize: 16,
  fontWeight: 700,
  color: '#f8fafc',
};

const closeBtnStyle = {
  background: 'none',
  border: 'none',
  color: '#64748b',
  cursor: 'pointer',
  fontSize: 14,
  padding: '2px 6px',
  borderRadius: 4,
};

const ankiSubStyle = {
  fontSize: 12,
  color: '#94a3b8',
  margin: '0 0 16px',
};

const ankiRowStyle = {
  display: 'flex',
  gap: 10,
};

const ankiBtnBase = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '12px 8px',
  borderRadius: 10,
  border: '2px solid',
  cursor: 'pointer',
  transition: 'transform 0.1s ease, opacity 0.1s ease',
  minHeight: 60,
};

// ─── Legacy styles ─────────────────────────────────────────────────────────────

const legacyOverlayStyle = {
  position: 'absolute',
  inset: 0,
  background: 'rgba(0,0,0,0.85)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 50,
  backdropFilter: 'blur(4px)',
};

const legacyCardStyle = {
  background: '#1e293b',
  borderRadius: 12,
  padding: '24px 28px',
  maxWidth: 560,
  width: '90%',
  border: '1px solid #334155',
  boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
};

const legacyHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 14,
};

const legacyLabelStyle = {
  fontSize: 11,
  letterSpacing: 1.5,
  textTransform: 'uppercase',
  color: '#94a3b8',
};

const legacyPromptStyle = {
  fontSize: 17,
  fontWeight: 600,
  color: '#f8fafc',
  margin: '0 0 22px',
  lineHeight: 1.5,
};

const legacyRatingRowStyle = {
  display: 'flex',
  gap: 10,
  justifyContent: 'stretch',
};

const legacyRatingBtnStyle = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '12px 6px',
  borderRadius: 8,
  border: '2px solid',
  background: 'transparent',
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  color: '#94a3b8',
  fontSize: 11,
};

const legacyContinueBtnStyle = {
  padding: '6px 18px',
  background: '#334155',
  color: '#94a3b8',
  border: '1px solid #475569',
  borderRadius: 6,
  fontSize: 13,
  cursor: 'pointer',
};
